/**
 * The customer dashboard's theme, built from the design tokens (SF-217 increment 1).
 *
 * The palette's roles (plan, "The palette's roles"):
 * - primary is ink and secondary is the second ink, so ordinary controls read as the design's
 *   black buttons and outlines rather than as a brand colour;
 * - the status keys are text-safe (./statusColours.ts); their pictogram colours are
 *   `palette.status`;
 * - the one attention colour is `palette.attention`, never a status and never a series;
 * - kinds of thing (folders, datasets, pipeline steps) take `palette.category`;
 * - charts take `palette.chart` (./chartColours.ts).
 *
 * The CSS custom properties the design's own stylesheet names (`--paper`, `--ink` and so on) are
 * set on the body for the current mode, so a class in styles/customer.css can use them too.
 */
import { createTheme, type Theme } from '@mui/material/styles';

import { STATUS_KEYS, statusColours } from './statusColours';
import {
  CATEGORY_NAMES,
  colourTokens,
  FONT_MONO,
  FONT_SANS,
  LEADING,
  RADIUS_PX,
  TEXT,
  type CategoryColour,
  type CategoryName,
  type ColourTokens,
  type ThemeMode,
} from './tokens';

declare module '@mui/material/styles' {
  interface Palette {
    attention: { main: string; ink: string; contrastText: string; wash: string };
    category: Record<CategoryName, CategoryColour>;
    status: ColourTokens['status'];
    chart: { seriesInk: string; series: readonly string[]; grid: string };
    rule: { main: string; strong: string };
    sunken: string;
  }
  interface PaletteOptions {
    attention?: Palette['attention'];
    category?: Palette['category'];
    status?: Palette['status'];
    chart?: Palette['chart'];
    rule?: Palette['rule'];
    sunken?: string;
  }
}

/** The design's names for the current mode's colours, as CSS custom properties. */
const customProperties = (tokens: ColourTokens, status: ReturnType<typeof statusColours>): Record<string, string> => ({
  '--status-failed-text': status.error.main,
  '--status-completed-text': status.success.main,
  '--paper': tokens.paper,
  '--surface': tokens.surface,
  '--sunken': tokens.sunken,
  '--ink': tokens.ink,
  '--ink-2': tokens.ink2,
  '--ink-3': tokens.ink3,
  '--rule': tokens.rule,
  '--rule-strong': tokens.ruleStrong,
  '--accent': tokens.accent,
  '--accent-ink': tokens.accentInk,
  '--focus': tokens.focus,
  '--font-sans': FONT_SANS,
  '--font-mono': FONT_MONO,
  ...Object.fromEntries(
    CATEGORY_NAMES.flatMap(name => [
      [`--c-${name}`, tokens.category[name].fill],
      [`--c-${name}-on`, tokens.category[name].on],
    ]),
  ),
});

/**
 * A floating label is the body size shrunk; MUI shrinks by 0.75, which from 14px is 10.5px, below the
 * design's smallest text (12px). This scale keeps it at 12px, the size MUI's own default reaches.
 */
const LABEL_SCALE = parseFloat(TEXT.label) / parseFloat(TEXT.body);

const heading = (fontSize: string, fontWeight = 600) => ({
  fontSize,
  fontWeight,
  lineHeight: LEADING.tight,
  letterSpacing: '-0.01em',
});

export const customerTheme = (mode: ThemeMode): Theme => {
  const tokens = colourTokens(mode);
  const status = statusColours(mode);
  const hairline = `1px solid ${tokens.rule}`;
  // The design's hover and current-row treatment: the paper, and for the current row an ink rule on
  // its leading edge. On paper every status colour keeps 4.5:1 (./statusColours.ts); MUI's grey
  // overlays for hover, focus and selection do not.
  const rowStates = {
    '&:hover, &.Mui-focusVisible': { backgroundColor: tokens.paper },
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
      backgroundColor: tokens.paper,
      boxShadow: `inset 3px 0 0 ${tokens.ink}`,
    },
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: tokens.ink, contrastText: tokens.surface },
      secondary: { main: tokens.ink2, contrastText: tokens.surface },
      ...status,
      // Status text and fills keep 4.5:1, so MUI's derived text on a status fill must too.
      contrastThreshold: 4.5,
      background: { default: tokens.paper, paper: tokens.surface },
      text: { primary: tokens.ink, secondary: tokens.ink2, disabled: tokens.ink3 },
      divider: tokens.rule,
      attention: { main: tokens.accent, ink: tokens.accentInk, contrastText: tokens.onAccent, wash: tokens.accentWash },
      category: tokens.category,
      status: tokens.status,
      chart: { seriesInk: tokens.seriesInk, series: tokens.series, grid: tokens.chartGrid },
      rule: { main: tokens.rule, strong: tokens.ruleStrong },
      sunken: tokens.sunken,
    },
    shape: { borderRadius: RADIUS_PX },
    typography: {
      fontFamily: FONT_SANS,
      h1: heading(TEXT.display),
      h2: heading(TEXT.display),
      h3: heading(TEXT.display),
      h4: heading(TEXT.display),
      h5: heading(TEXT.title),
      h6: heading(TEXT.lead),
      subtitle1: { fontSize: TEXT.lead, fontWeight: 500, lineHeight: LEADING.body },
      subtitle2: { fontSize: TEXT.body, fontWeight: 600, lineHeight: LEADING.body },
      body1: { fontSize: TEXT.body, lineHeight: LEADING.body },
      body2: { fontSize: TEXT.data, lineHeight: LEADING.body },
      caption: { fontSize: TEXT.label, lineHeight: LEADING.body },
      overline: { fontSize: TEXT.label, fontWeight: 500 },
      button: { fontSize: TEXT.body, fontWeight: 500 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            ...customProperties(tokens, status),
            WebkitFontSmoothing: 'antialiased',
          },
          'code, kbd, pre, samp': { fontFamily: FONT_MONO },
          ':focus-visible': { outline: `2px solid ${tokens.focus}`, outlineOffset: 2 },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: { boxShadow: 'none', borderBottom: hairline, backgroundColor: tokens.surface, color: tokens.ink },
        },
      },
      MuiDrawer: {
        styleOverrides: { paper: { borderRight: hairline, backgroundColor: tokens.surface } },
      },
      MuiPaper: {
        styleOverrides: {
          // MUI lightens raised paper in dark mode with a gradient; the surface token is the colour.
          root: { backgroundImage: 'none' },
          // In-page panels are the design's: a surface on a hairline, flat. Menus, popovers and
          // dialogs (elevation 8 and 24) keep MUI's shadow so they read as above the page.
          elevation1: { boxShadow: 'none', border: hairline },
          elevation2: { boxShadow: 'none', border: hairline },
          outlined: { border: hairline },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 0,
            // The design's disabled button: the third ink on no fill, inside a rule.
            '&.Mui-disabled': { color: tokens.ink3 },
          },
          contained: { '&.Mui-disabled': { backgroundColor: 'transparent', boxShadow: `inset 0 0 0 1px ${tokens.rule}` } },
          outlined: { '&.Mui-disabled': { borderColor: tokens.rule } },
        },
      },
      MuiListItemButton: { styleOverrides: { root: rowStates } },
      MuiMenuItem: { styleOverrides: { root: rowStates } },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&.MuiTableRow-hover:hover': { backgroundColor: tokens.paper },
            '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: tokens.paper },
          },
        },
      },
      MuiAlert: {
        // MUI derives an alert's fill and text from `light` again, which turns the status wash
        // nearly white; the wash is the fill, the text is ink and the icon wears the status.
        styleOverrides: Object.fromEntries(
          STATUS_KEYS.flatMap(key => {
            const capitalised = `${key[0].toUpperCase()}${key.slice(1)}`;
            return [
              [`standard${capitalised}`, { backgroundColor: status[key].light, color: tokens.ink, '& .MuiAlert-icon': { color: status[key].main } }],
              [`outlined${capitalised}`, { borderColor: status[key].main, color: tokens.ink, '& .MuiAlert-icon': { color: status[key].main } }],
            ];
          }),
        ),
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { '&.MuiInputLabel-outlined.MuiInputLabel-shrink': { transform: `translate(14px, -9px) scale(${LABEL_SCALE})` } },
        },
      },
      MuiOutlinedInput: {
        // The gap in the outline is sized by a hidden copy of the label at this scale.
        styleOverrides: { notchedOutline: { '& legend': { fontSize: `${LABEL_SCALE}em` } } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: hairline },
          head: { color: tokens.ink2, fontWeight: 600 },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: tokens.rule } } },
      MuiBackdrop: {
        styleOverrides: { root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: tokens.scrim } } },
      },
    },
  });
};
